/**
 * Iter 245 — Gestion des fournisseurs.
 */
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Truck, Plus, Pencil, Trash2, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", contact_name: "", email: "", phone: "", address: "",
  payment_terms: "30j fin de mois", lead_days_avg: 7, notes: "",
};

export default function SuppliersPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/suppliers");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setForm(empty); setModal({ mode: "create" }); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setModal({ mode: "edit", supplier: s }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form, lead_days_avg: Number(form.lead_days_avg) || 7 };
      if (modal.mode === "create") {
        await api.post("/inventory/suppliers", payload);
        toast.success("Fournisseur créé");
      } else {
        await api.put(`/inventory/suppliers/${modal.supplier.supplier_id}`, payload);
        toast.success("Fournisseur mis à jour");
      }
      setModal(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer ${s.name} ?`)) return;
    try {
      await api.delete(`/inventory/suppliers/${s.supplier_id}`);
      toast.success("Fournisseur supprimé");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="suppliers-page">
      <PageHeader
        icon={<Truck />}
        title="Fournisseurs"
        description={`${items.length} fournisseur(s) référencé(s).`}
        actions={<Button onClick={openCreate} data-testid="btn-create-supplier" className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c]"><Plus className="w-4 h-4 mr-2" /> Ajouter</Button>}
      />

      {items.length === 0 ? (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-12 text-center text-slate-500">Aucun fournisseur. Cliquez "Ajouter" pour commencer.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((s) => (
            <div key={s.supplier_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`supplier-${s.supplier_id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-bold truncate">{s.name}</h3>
                  {s.contact_name && <p className="text-xs text-slate-400">{s.contact_name}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(s)} className="hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-xs">
                {s.email && <div className="flex items-center gap-2 text-slate-400"><Mail className="w-3 h-3" /> {s.email}</div>}
                {s.phone && <div className="flex items-center gap-2 text-slate-400"><Phone className="w-3 h-3" /> {s.phone}</div>}
                <div className="text-slate-500 mt-2"><span className="text-slate-600">Paiement :</span> {s.payment_terms}</div>
                <div className="text-slate-500"><span className="text-slate-600">Délai moyen :</span> {s.lead_days_avg} jours</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle>{modal?.mode === "create" ? "Nouveau fournisseur" : "Modifier le fournisseur"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="Nom *" full><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="field-supplier-name" /></Field>
            <Field label="Contact"><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Téléphone"><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Délai (jours)"><Input type="number" value={form.lead_days_avg} onChange={e => setForm({ ...form, lead_days_avg: e.target.value })} /></Field>
            <Field label="Adresse" full><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Conditions de paiement" full><Input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} /></Field>
            <Field label="Notes" full>
              <textarea className="w-full bg-[#0b0811] border border-white/10 rounded px-2 py-2 text-sm min-h-[60px]" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setModal(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c]" data-testid="btn-save-supplier">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <label className="text-[11px] text-slate-500 mb-1 block">{label}</label>
    {children}
  </div>
);
